import React, { useEffect, useState } from 'react'
import { importKeyFromBase64, decryptBuffer, parseTransferFragment } from '../utils/crypto'
import Button from '../components/ui/Button'
import styles from './Receive.module.css'

export default function Receive() {
  const [status, setStatus] = useState('parsing')
  const [error, setError] = useState(null)
  const [decryptedUrl, setDecryptedUrl] = useState(null)
  const [mimeType, setMimeType] = useState(null)
  const [fileName, setFileName] = useState('file')

  useEffect(() => {
    const run = async () => {
      try {
        const fragment = window.location.hash
        if (!fragment || fragment.length < 10) {
          throw new Error('Invalid transfer link. Please check the URL and try again.')
        }

        const parsed = parseTransferFragment(fragment)
        if (!parsed.keyBase64 || !parsed.ivBase64 || !parsed.fileId || !parsed.mimeType) {
          throw new Error('Invalid transfer link. Required parameters are missing.')
        }

        setStatus('downloading')
        const res = await fetch(`/api/file/${parsed.fileId}`)
        if (res.status === 410) {
          throw new Error('This file has expired. Files are automatically deleted after 15 minutes.')
        }
        if (!res.ok) {
          throw new Error('Failed to download file. Please try again.')
        }

        const encryptedBuffer = await res.arrayBuffer()
        const iv = Uint8Array.from(atob(parsed.ivBase64), (c) => c.charCodeAt(0))

        setStatus('decrypting')
        const key = await importKeyFromBase64(parsed.keyBase64)
        const decrypted = await decryptBuffer(encryptedBuffer, key, iv)

        const blob = new Blob([decrypted], { type: parsed.mimeType })
        const url = URL.createObjectURL(blob)
        setDecryptedUrl(url)
        setMimeType(parsed.mimeType)
        setFileName(`verifylink-file.${parsed.mimeType.split('/')[1] || 'bin'}`)
        setStatus('ready')
      } catch (err) {
        setStatus('error')
        setError(err.message || 'Something went wrong.')
      }
    }
    run()
  }, [])

  const handleSave = async () => {
    if (!decryptedUrl) return
    try {
      const response = await fetch(decryptedUrl)
      const blob = await response.blob()
      const file = new File([blob], fileName, { type: mimeType })

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fileName })
      } else {
        const a = document.createElement('a')
        a.href = decryptedUrl
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    } catch (err) {
      // Fallback to download
      const a = document.createElement('a')
      a.href = decryptedUrl
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  const isImage = mimeType?.startsWith('image/')
  const isVideo = mimeType?.startsWith('video/')

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {status === 'parsing' && (
          <div className={styles.center}>
            <div className={styles.spinner} aria-hidden="true" />
            <p>Reading transfer link...</p>
          </div>
        )}

        {status === 'downloading' && (
          <div className={styles.center}>
            <div className={styles.spinner} aria-hidden="true" />
            <p>Downloading encrypted file...</p>
          </div>
        )}

        {status === 'decrypting' && (
          <div className={styles.center}>
            <div className={styles.spinner} aria-hidden="true" />
            <p>Decrypting in your browser...</p>
          </div>
        )}

        {status === 'error' && (
          <div className={styles.center}>
            <h1 className={styles.title}>Unable to open file</h1>
            <p className={styles.errorText}>{error}</p>
            <Button onClick={() => window.location.reload()}>Try again</Button>
          </div>
        )}

        {status === 'ready' && (
          <div className={styles.ready}>
            <h1 className={styles.title}>File received</h1>
            <div className={styles.preview}>
              {isImage && (
                <img
                  src={decryptedUrl}
                  alt="Received file"
                  className={styles.media}
                />
              )}
              {isVideo && (
                <video
                  src={decryptedUrl}
                  controls
                  className={styles.media}
                  aria-label="Received video"
                />
              )}
              {!isImage && !isVideo && (
                <div className={styles.filePlaceholder}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p>{fileName}</p>
                </div>
              )}
            </div>
            <Button size="lg" fullWidth onClick={handleSave}>
              Save to device
            </Button>
          </div>
        )}
      </div>
    </main>
  )
}
